import React from 'react';
import { motion } from 'framer-motion';
import { FaHandshake, FaSearch, FaFileSignature, FaCalendarAlt, FaCode, FaRocket } from 'react-icons/fa';

// Which discipline each step draws on — colours match the hero cards.
const areas = {
    bd: { label: 'Business Development', className: 'bg-pink-100 text-pink-700' },
    pm: { label: 'Project Management', className: 'bg-gray-200 text-gray-800' },
    dev: { label: 'Software Development', className: 'bg-olive-100 text-olive-700' },
};

const steps = [
    {
        icon: FaHandshake,
        title: 'Connect',
        text: 'Meeting new clients and partners at conferences, events and through my network.',
        tags: ['bd'],
    },
    {
        icon: FaSearch,
        title: 'Discover',
        text: 'Client meetings to understand the business need and gather requirements.',
        tags: ['bd', 'pm'],
    },
    {
        icon: FaFileSignature,
        title: 'Propose',
        text: 'Solution scenarios, proposals, statements of work and project plans.',
        tags: ['bd', 'dev'],
    },
    {
        icon: FaCalendarAlt,
        title: 'Plan',
        text: 'Turning requirements into user stories, tasks, schedules and clear deliverables.',
        tags: ['pm', 'dev'],
    },
    {
        icon: FaCode,
        title: 'Build',
        text: 'Coordinating the development team — and building hands-on when it helps.',
        tags: ['pm', 'dev'],
    },
    {
        icon: FaRocket,
        title: 'Deliver & Support',
        text: 'Quality checks, on-time delivery and follow-up support that keeps the client relationship going.',
        tags: ['pm', 'bd'],
    },
];

const Process = () => (
    <section id="process" className="py-20 bg-cream">
        <div className="container mx-auto px-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="mb-12 text-center"
            >
                <h2 className="text-4xl font-bold text-gray-800 mb-4">How I Work</h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                    One person from the first handshake to the final delivery — business, management and engineering in the same process.
                </p>
                <div className="w-24 h-1 bg-pink-500 mx-auto mt-4"></div>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {Object.values(areas).map((a) => (
                        <span key={a.label} className={`rounded-full px-3 py-1 text-xs font-semibold ${a.className}`}>
                            {a.label}
                        </span>
                    ))}
                </div>
            </motion.div>

            <ol className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {steps.map((step, i) => {
                    const Icon = step.icon;
                    return (
                        <motion.li
                            key={step.title}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            whileHover={{ y: -10 }}
                            viewport={{ once: true }}
                            className="relative flex flex-col rounded-2xl border border-cream-200 bg-white p-6 shadow-[0_12px_30px_-18px_rgba(41,37,31,0.25)] transition-shadow hover:shadow-xl"
                        >
                            <div className="flex items-center justify-between">
                                <span className="grid h-12 w-12 place-items-center rounded-full bg-ink text-lg text-cream">
                                    <Icon aria-hidden="true" />
                                </span>
                                <span className="font-heading text-4xl font-bold text-cream-200">
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                            </div>
                            <h3 className="mt-5 text-xl font-bold text-gray-800">{step.title}</h3>
                            <p className="mt-2 flex-1 text-gray-600">{step.text}</p>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {step.tags.map((t) => (
                                    <span key={t} className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${areas[t].className}`}>
                                        {areas[t].label}
                                    </span>
                                ))}
                            </div>
                        </motion.li>
                    );
                })}
            </ol>
        </div>
    </section>
);

export default Process;
